import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ShareableWorkout } from './ShareWorkoutCard';
import { ShareTemplateClassic, ShareTemplateMinimal, ShareTemplateBold } from './share';
import { Loader2, ChevronLeft, ChevronRight } from 'lucide-react';
import { ShareIcon } from './icons/ShareIcon';
import { toast } from 'sonner';
import html2canvas from 'html2canvas';
import { Capacitor } from '@capacitor/core';
import { Share } from '@capacitor/share';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { Media } from '@capacitor-community/media';

type TemplateType = 'classic' | 'minimal' | 'bold';

const TEMPLATE_NAMES: Record<TemplateType, string> = {
  classic: 'Classic',
  minimal: 'Transparent',
  bold: 'Bold',
};

interface ShareWorkoutModalProps {
  isOpen: boolean;
  onClose: () => void;
  workout: ShareableWorkout | null;
}

export const ShareWorkoutModal = ({ isOpen, onClose, workout }: ShareWorkoutModalProps) => {
  const cardRef = useRef<HTMLDivElement>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedImage, setGeneratedImage] = useState<string | null>(null);
  const [isSharing, setIsSharing] = useState(false);
  const [isSavingToPhotos, setIsSavingToPhotos] = useState(false);
  const [saveSuccessToast, setSaveSuccessToast] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<TemplateType>('classic');

  const templates: TemplateType[] = ['classic', 'minimal', 'bold'];

  // Generate image when modal opens or template changes
  // Render template in portal to body for consistent capture (avoids modal layout affecting html2canvas)
  useEffect(() => {
    if (isOpen && workout) {
      setGeneratedImage(null);
      // Longer delay when modal first opens to ensure layout is settled (fixes gym completion flow)
      const timer = setTimeout(() => {
        if (cardRef.current) {
          generateImage();
        }
      }, 350);
      return () => clearTimeout(timer);
    } else if (!isOpen) {
      setGeneratedImage(null);
      setSelectedTemplate('classic');
      setSaveSuccessToast(false);
    }
  }, [isOpen, workout, selectedTemplate]);

  const generateImage = async () => {
    if (!cardRef.current) return;
    
    setIsGenerating(true);
    try {
      // Wait for fonts and images to load
      await new Promise(resolve => setTimeout(resolve, 400));
      
      const canvas = await html2canvas(cardRef.current, {
        backgroundColor: selectedTemplate === 'minimal' ? null : '#0a0a0a',
        scale: 2, // Higher quality
        useCORS: true,
        logging: false,
        scrollX: 0,
        scrollY: 0,
      });
      
      const dataUrl = canvas.toDataURL('image/png');
      setGeneratedImage(dataUrl);
    } catch (error) {
      console.error('Error generating image:', error);
      toast.error('Failed to generate image');
    } finally {
      setIsGenerating(false);
    }
  };

  const downloadFallback = () => {
    if (!generatedImage) return;
    // Programmatic download doesn't work in Android WebView - use Share instead on native
    if (Capacitor.isNativePlatform()) {
      toast.info('Choose "Save image" or "Save to device" from the share options');
      handleShare();
      return;
    }
    const link = document.createElement('a');
    link.download = `straiv-workout-${workout?.id || 'summary'}-${selectedTemplate}.png`;
    link.href = generatedImage;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('Image saved to downloads');
  };

  const getAlbumIdentifierForSave = async (): Promise<string | undefined> => {
    if (Capacitor.getPlatform() !== 'android') return undefined;
    try {
      const { albums } = await Media.getAlbums();
      if (albums && albums.length > 0) {
        const camera = albums.find((a) => a.name === 'Camera' || a.name === 'DCIM');
        const pics = albums.find((a) => a.name === 'Pictures');
        const straiv = albums.find((a) => a.name === 'Straiv');
        const first = albums[0];
        return (camera || pics || straiv || first)?.identifier;
      }
      const { path } = await Media.getAlbumsPath();
      if (path) {
        try {
          await Media.createAlbum({ name: 'Straiv' });
        } catch {
          // Album may already exist
        }
        const { albums } = await Media.getAlbums();
        const straiv = albums?.find((a) => a.name === 'Straiv');
        if (straiv?.identifier) return straiv.identifier;
        return path ? `${path}/Straiv` : undefined;
      }
    } catch (e) {
      console.warn('[Share] Could not get album:', e);
    }
    return undefined;
  };

  const handleSaveToPhotos = async () => {
    if (!generatedImage) return;
    setIsSavingToPhotos(true);
    setSaveSuccessToast(false);
    toast.info('Saving to Photos...', { duration: 1500, style: { zIndex: 2147483647 } });
    try {
      const albumIdentifier = await getAlbumIdentifierForSave();
      const opts: { path: string; albumIdentifier?: string; fileName?: string } = {
        path: generatedImage,
        fileName: `straiv-workout-${workout?.id || 'summary'}-${selectedTemplate}`.replace(/[^a-zA-Z0-9._-]/g, '_'),
      };
      if (albumIdentifier) opts.albumIdentifier = albumIdentifier;
      await Media.savePhoto(opts);
      setSaveSuccessToast(true);
      toast.success('Photo saved to library!', { duration: 4000, style: { zIndex: 2147483647 } });
      setTimeout(() => setSaveSuccessToast(false), 4000);
    } catch (error) {
      console.error('Error saving to Photos:', error);
      toast.error('Failed to save to Photos');
      downloadFallback();
    } finally {
      setIsSavingToPhotos(false);
    }
  };

  const handleShare = async () => {
    if (!generatedImage) {
      toast.error('Please wait for the image to load');
      return;
    }

    setIsSharing(true);
    try {
      const fileName = `straiv-workout-${workout?.id || 'summary'}-${selectedTemplate}.png`;
      const shareText = `Just crushed a ${workout?.duration_minutes} min ${workout?.title} workout!`;

      if (Capacitor.isNativePlatform()) {
        // Try Web Share API first (works on Android Chrome WebView, iOS Safari)
        if (navigator.share && navigator.canShare) {
          try {
            const response = await fetch(generatedImage);
            const blob = await response.blob();
            const file = new File([blob], fileName, { type: 'image/png' });
            if (navigator.canShare({ files: [file] })) {
              await navigator.share({
                title: 'My STRAIV Workout',
                text: shareText,
                files: [file],
              });
              toast.success('Shared successfully!');
              return;
            }
          } catch (webShareErr: any) {
            if (webShareErr.name !== 'AbortError') {
              console.warn('[Share] Web Share API failed, trying native:', webShareErr);
            }
          }
        }

        // Use Capacitor Share plugin (iOS & Android)
        const base64Data = generatedImage.replace(/^data:image\/png;base64,/, '');
        const path = fileName.replace(/[^a-zA-Z0-9._-]/g, '_');
        await Filesystem.writeFile({
          path,
          data: base64Data,
          directory: Directory.Cache,
        });
        const { uri } = await Filesystem.getUri({
          path,
          directory: Directory.Cache,
        });
        const isAndroid = Capacitor.getPlatform() === 'android';
        // Android Share plugin requires file:// URLs; getUri returns content:// for Cache.
        if (isAndroid && uri.startsWith('content://')) {
          const bases = [
            'file:///data/data/com.striveai.fitness/cache',
            'file:///data/user/0/com.striveai.fitness/cache',
          ];
          const pathsToTry = bases.flatMap((b) => [`${b}/${path}`, `${b}/files/${path}`]);
          let shared = false;
          for (const fileUrl of pathsToTry) {
            try {
              await Share.share({
                title: 'My STRAIV Workout',
                text: shareText,
                files: [fileUrl],
                dialogTitle: 'Share workout',
              });
              shared = true;
              break;
            } catch {
              // try next path
            }
          }
          if (!shared) throw new Error('Share failed');
        } else {
          await Share.share({
            title: 'My STRAIV Workout',
            text: shareText,
            url: uri,
            dialogTitle: 'Share workout',
          });
        }
        toast.success('Shared successfully!');
      } else if (navigator.share && navigator.canShare) {
        const response = await fetch(generatedImage);
        const blob = await response.blob();
        const file = new File([blob], fileName, { type: 'image/png' });
        if (navigator.canShare({ files: [file] })) {
          await navigator.share({
            title: 'My STRAIV Workout',
            text: shareText,
            files: [file],
          });
          toast.success('Shared successfully!');
        } else {
          downloadFallback();
        }
      } else {
        downloadFallback();
      }
    } catch (error: any) {
      const isUserCancel = error?.name === 'AbortError' || 
        (typeof error?.message === 'string' && error.message.toLowerCase().includes('cancel'));
      if (!isUserCancel) {
        console.error('Error sharing:', error);
        toast.error('Failed to share');
        if (Capacitor.isNativePlatform()) {
          downloadFallback();
        }
      }
    } finally {
      setIsSharing(false);
    }
  };

  const navigateTemplate = (direction: 'prev' | 'next') => {
    const currentIndex = templates.indexOf(selectedTemplate);
    if (direction === 'prev') {
      const newIndex = currentIndex === 0 ? templates.length - 1 : currentIndex - 1;
      setSelectedTemplate(templates[newIndex]);
    } else {
      const newIndex = currentIndex === templates.length - 1 ? 0 : currentIndex + 1;
      setSelectedTemplate(templates[newIndex]);
    }
  };

  const renderTemplate = () => {
    switch (selectedTemplate) {
      case 'classic':
        return <ShareTemplateClassic ref={cardRef} workout={workout} />;
      case 'minimal':
        return <ShareTemplateMinimal ref={cardRef} workout={workout} />;
      case 'bold':
        return <ShareTemplateBold ref={cardRef} workout={workout} />;
    }
  };

  if (!workout) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent 
        className="max-w-lg max-h-[90vh] overflow-y-auto z-[100000]"
        overlayClassName="z-[100000]"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShareIcon className="h-5 w-5 text-primary" />
            Share Workout
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Template in portal to body - consistent position for html2canvas (fixes gym completion flow) */}
          {isOpen && workout && createPortal(
            <div 
              aria-hidden
              style={{ 
                position: 'fixed',
                left: 0,
                top: 0,
                width: '1080px',
                minWidth: '1080px',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'flex-start',
                boxSizing: 'border-box',
                opacity: 0.01,
                pointerEvents: 'none',
                zIndex: -1,
                overflow: 'hidden',
              }}
            >
              {renderTemplate()}
            </div>,
            document.body
          )}

          {/* Template Selector with Navigation */}
          <div className="flex items-center justify-center gap-4 mb-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigateTemplate('prev')}
              className="h-8 w-8 touch-manipulation min-w-[44px] min-h-[44px]"
            >
              <ChevronLeft className="h-5 w-5" />
            </Button>
            <div className="text-center min-w-[100px]">
              <p className="font-medium text-sm">{TEMPLATE_NAMES[selectedTemplate]}</p>
              <div className="flex justify-center gap-1.5 mt-1">
                {templates.map((t) => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => setSelectedTemplate(t)}
                    className={`w-2 h-2 rounded-full transition-all touch-manipulation min-w-[20px] min-h-[20px] ${
                      t === selectedTemplate 
                        ? 'bg-primary scale-110' 
                        : 'bg-muted-foreground/30'
                    }`}
                  />
                ))}
              </div>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigateTemplate('next')}
              className="h-8 w-8 touch-manipulation min-w-[44px] min-h-[44px]"
            >
              <ChevronRight className="h-5 w-5" />
            </Button>
          </div>

          {/* Generated Image Preview - centered for iOS */}
          <div className="flex justify-center items-center w-full">
            {isGenerating ? (
              <div className="w-full h-[380px] flex items-center justify-center bg-muted/20 rounded-xl">
                <div className="text-center space-y-2">
                  <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
                  <p className="text-sm text-muted-foreground">Generating image...</p>
                </div>
              </div>
            ) : generatedImage ? (
              <img 
                src={generatedImage} 
                alt="Workout Summary" 
                className="max-w-full h-auto rounded-xl shadow-lg block mx-auto"
                style={{ maxHeight: '380px', objectFit: 'contain' }}
              />
            ) : (
              <div className="w-full h-[380px] flex items-center justify-center bg-muted/20 rounded-xl">
                <p className="text-sm text-muted-foreground">Loading preview...</p>
              </div>
            )}
          </div>

          {/* iOS: single "Save & Share" button; Android: "Share" + "Save to Photos"; Web: Share + Download */}
          <Button
            onClick={handleShare}
            disabled={!generatedImage || isSharing}
            className="h-14 w-full text-base bg-gradient-primary text-black font-semibold touch-manipulation"
          >
            <span className="flex flex-1 items-center justify-center gap-2">
              {isSharing ? (
                <Loader2 className="h-5 w-5 animate-spin shrink-0" />
              ) : (
                <ShareIcon className="h-5 w-5 shrink-0 text-primary" />
              )}
              {Capacitor.getPlatform() === 'ios' ? 'Save & Share' : 'Share'}
            </span>
          </Button>

          {/* Android only: Save to Photos */}
          {generatedImage && Capacitor.getPlatform() === 'android' && (
            <Button
              variant="outline"
              onClick={handleSaveToPhotos}
              disabled={isSavingToPhotos}
              className="w-full touch-manipulation"
            >
              {isSavingToPhotos ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : null}
              Save to Photos
            </Button>
          )}

          {/* Web only: Download fallback */}
          {generatedImage && !Capacitor.isNativePlatform() && (
            <Button
              variant="outline"
              onClick={downloadFallback}
              className="w-full touch-manipulation"
            >
              Save to device (download)
            </Button>
          )}

          <p className="text-xs text-center text-muted-foreground">
            {Capacitor.getPlatform() === 'ios'
              ? 'Share or save to Photos from the share sheet.'
              : 'Share to Instagram, Snapchat, or save to your photo library.'}
          </p>

          {/* In-modal success toast - visible above modal when save completes */}
          {saveSuccessToast && (
            <div
              className="fixed left-1/2 top-20 -translate-x-1/2 z-[2147483647] animate-in fade-in slide-in-from-top-2 duration-300 rounded-lg border bg-background px-4 py-3 shadow-lg"
              role="status"
              aria-live="polite"
            >
              <p className="text-sm font-medium text-foreground">Photo saved to library!</p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
